/**
 * Réservations › Mes réservations — the member's own bookings.
 *
 * Two sections: « À venir » (pending or confirmed, not yet over on the ORG's
 * clock — soonest first) and « Passées et annulées » (everything else — most
 * recent first), the second collapsed by default because it only grows and
 * is rarely what the member came for.
 *
 * A row opens the shared `BookingDetailSheet` in member mode, which owns
 * cancelling and « Inviter des visiteurs ».
 */
import type { RouterOutputs } from "@gately/api";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ChevronDown, ChevronLeft, Users } from "lucide-react";

import { cn } from "@gately/ui";
import { Button } from "@gately/ui/button";
import { Card } from "@gately/ui/card";
import { EmptyState } from "@gately/ui/empty-state";
import { StatusBadge } from "@gately/ui/status-badge";
import { bookingStatusLabel } from "@gately/validators";

import { BookingDetailSheet } from "~/components/booking/BookingDetailSheet";
import {
  bookingRangeLabel,
  bookingStatusTone,
  isUpcomingBooking,
  orgClock,
} from "~/lib/booking-format";
import { orpc } from "~/lib/orpc";
import { useOrgTimeZone } from "~/lib/use-me";
import { resolvePermissions } from "~/lib/use-permissions";

export const Route = createFileRoute(
  "/_dashboard/reservations/mes-reservations",
)({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).hasAmenities) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: MesReservationsPage,
});

type Booking = RouterOutputs["booking"]["mine"][number];

function MesReservationsPage() {
  const timeZone = useOrgTimeZone();
  const query = useQuery(orpc.booking.mine.queryOptions());
  const [openId, setOpenId] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);

  const clock = orgClock(timeZone);
  const all = query.data ?? [];
  // `booking.mine` is newest first; the future reads soonest first.
  const upcoming = all.filter((b) => isUpcomingBooking(b, clock)).reverse();
  const past = all.filter((b) => !isUpcomingBooking(b, clock));

  function renderUpcoming() {
    if (query.data) {
      if (upcoming.length === 0) {
        return (
          <Card className="flex flex-col items-center p-0 pb-8">
            <EmptyState title="Aucune réservation à venir" className="pb-4" />
            <Button asChild>
              <Link to="/reservations">Réserver</Link>
            </Button>
          </Card>
        );
      }
      return <BookingList rows={upcoming} onOpen={setOpenId} />;
    }
    if (query.isError) {
      return (
        <Card className="p-0">
          <EmptyState
            title="Impossible de charger vos réservations."
            description="Vérifiez votre connexion puis réessayez."
          />
        </Card>
      );
    }
    return <Card className="h-32 animate-pulse bg-neutral-100/60" />;
  }

  return (
    <div className="px-7 py-6">
      <div className="mb-5">
        <Link
          to="/reservations"
          className="text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1 text-sm"
        >
          <ChevronLeft className="size-4" />
          Réserver
        </Link>
        <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
          Mes réservations
        </h1>
      </div>

      <div className="space-y-8">
        <section className="space-y-3" aria-labelledby="mes-resa-a-venir">
          <h2 id="mes-resa-a-venir" className="text-base font-semibold">
            À venir
          </h2>
          {renderUpcoming()}
        </section>

        {past.length > 0 ? (
          <section className="space-y-3">
            <h2 className="text-base font-semibold">
              <button
                type="button"
                aria-expanded={showPast}
                aria-controls="mes-resa-passees"
                onClick={() => setShowPast((v) => !v)}
                className="hover:text-foreground/80 inline-flex items-center gap-1.5"
              >
                Passées et annulées
                <span className="text-muted-foreground text-sm font-normal tabular-nums">
                  ({past.length})
                </span>
                <ChevronDown
                  className={cn(
                    "text-muted-foreground size-4 transition-transform",
                    showPast && "rotate-180",
                  )}
                />
              </button>
            </h2>
            {showPast ? (
              <div id="mes-resa-passees">
                <BookingList rows={past} onOpen={setOpenId} />
              </div>
            ) : null}
          </section>
        ) : null}
      </div>

      <BookingDetailSheet
        bookingId={openId}
        open={openId !== null}
        onOpenChange={(open) => {
          if (!open) setOpenId(null);
        }}
        mode="member"
      />
    </div>
  );
}

function BookingList({
  rows,
  onOpen,
}: {
  rows: Booking[];
  onOpen: (id: string) => void;
}) {
  return (
    <Card className="divide-y overflow-hidden p-0">
      {rows.map((b) => (
        <button
          key={b.id}
          type="button"
          onClick={() => onOpen(b.id)}
          className="flex w-full flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3.5 text-left hover:bg-neutral-50 focus-visible:bg-neutral-50 focus-visible:outline-none"
        >
          <span className="min-w-0 flex-1">
            <span className="block font-medium tabular-nums">
              {bookingRangeLabel(b)}
            </span>
            <span className="text-muted-foreground block truncate text-sm">
              {[b.amenityName, b.locationLabel].filter(Boolean).join(" · ")}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {b.inviteCount > 0 ? (
              <span className="text-muted-foreground inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs">
                <Users className="size-3" />
                {b.inviteCount === 1
                  ? "1 invitation"
                  : `${b.inviteCount} invitations`}
              </span>
            ) : null}
            <StatusBadge tone={bookingStatusTone(b.status)}>
              {bookingStatusLabel(b.status)}
            </StatusBadge>
          </span>
        </button>
      ))}
    </Card>
  );
}
